import { cloneElement, type InputHTMLAttributes, type ReactElement } from 'react';
import { ChipTag, type ChipTagProps } from '~/components/new/Chip';
import type IconAction from '~/components/new/IconAction';
import '~/styles/components/new/control-card.scss';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';
import { CheckBoxInput, type CheckBoxInputProps } from './forms/CheckBox';
import { RadioInput, type RadioInputProps } from './forms/Choice';
import { SwitchInput, type SwitchInputProps } from './Switch';

type CheckboxComponent = ReactElement<CheckBoxInputProps, typeof CheckBoxInput>;
type RadioComponent = ReactElement<RadioInputProps, typeof RadioInput>;
type SwitchComponent = ReactElement<SwitchInputProps, typeof SwitchInput>;

export type ControlCardProps = {
    inputComponent: CheckboxComponent | RadioComponent | SwitchComponent;
    className?: string;
    title: string;
    subtitle?: string;
    disabled?: boolean;
    tag?: string | ReactElement<typeof ChipTag>;
    tagColor?: ChipTagProps['color'];
    tagIcon?: ChipTagProps['icon'];
    surface?: 'primary' | 'secondary';
    onClick?: (value: boolean) => void;
    actions?: ReactElement<typeof IconAction> | ReactElement<typeof IconAction>[];
} & DataTestAttributes;

export default function ControlCard({
    inputComponent,
    className = '',
    title,
    subtitle,
    disabled = false,
    tag,
    tagColor = 'grey',
    tagIcon,
    surface = 'primary',
    onClick,
    actions,
    ['data-test-component']: testComponent = 'ui/ControlCard',
    ['data-test-name']: testName,
}: ControlCardProps) {
    const isSwitch = inputComponent.type === SwitchInput;

    /* injects disabled into input component */
    const inputComponentWithState = cloneElement(inputComponent, { disabled, readOnly: true });

    return (
        <div
            data-test-component={testComponent}
            data-test-name={testName}
            className={classNames({
                [`control-card surface-${surface}`]: true,
                disabled,
                [className]: Boolean(className),
            })}
            onClick={
                !disabled
                    ? () =>
                          // since ControlCard ignores the checked state (which is part of the input component) it uses its props to get it.
                          onClick?.(
                              !(inputComponent.props as InputHTMLAttributes<HTMLInputElement>)
                                  .checked,
                          )
                    : undefined
            }
            role='button'>
            {!isSwitch && (
                <div
                    className='control-card-input'
                    data-test-name='input'>
                    {inputComponentWithState}
                </div>
            )}
            <div className='control-card-content'>
                <div className='control-card-title'>
                    <span data-test-name='title'>{title}</span>
                    <div
                        className='control-card-actions'
                        data-test-name='actions'>
                        {typeof tag === 'string' ? (
                            <ChipTag
                                className='control-card-tag'
                                icon={tagIcon}
                                color={tagColor}>
                                {tag}
                            </ChipTag>
                        ) : (
                            tag
                        )}
                        <div className='flex-inline items-center justify-center gap-3 hide-empty'>
                            {actions}
                        </div>
                    </div>
                </div>
                {subtitle && (
                    <span
                        className='control-card-subtitle'
                        data-test-name='subtitle'>
                        {subtitle}
                    </span>
                )}
            </div>
            {isSwitch && (
                <div
                    className='control-card-input-switch'
                    data-test-name='switch'>
                    {inputComponentWithState}
                </div>
            )}
        </div>
    );
}
